// analytics.js — on-device training intelligence. Pure functions over app state
// (no React, no I/O) so they are unit-testable in Node and cheap to memoize.
//
// Everything here is derived from the user's own logged sets:
//   - estimated 1RM per set / per session / trend per exercise
//   - weekly hard sets per muscle group vs evidence-based volume landmarks
//   - plateau detection (e1RM stalled across recent exposures)
//   - readiness / fatigue signal (recent performance vs expected, RPE creep)
//   - deload recommendation
//   - RPE-based load targeting (RIR table)
//   - consistency / streaks
//
// Set shape: { exerciseId, peId, setIdx, w (lb), reps, rpe?, ts, type? }
// type: 'warmup' sets are excluded from every working-set statistic.

const DAY = 86400000;
const WEEK = 7 * DAY;

// ——— Strength estimates ————————————————————————————————————————————————

// Blended e1RM: Epley overestimates at high reps, Brzycki underestimates at
// low reps; averaging tracks tested maxes better across 1–12 reps. Reps above
// 15 are too unreliable to extrapolate, so they are capped.
export function e1rm(w, reps) {
  if (!(w > 0) || !(reps > 0)) return 0;
  if (reps === 1) return w;
  const r = Math.min(reps, 15);
  const epley = w * (1 + r / 30);
  const brzycki = w * 36 / (37 - r);
  return (epley + brzycki) / 2;
}

// Percent of 1RM achievable for (reps, rpe) — Tuchscherer-style RPE chart,
// interpolated. RPE 10 = no reps in reserve.
const RPE_TABLE = {
  // reps: [rpe10, rpe9, rpe8, rpe7, rpe6]
  1: [1.000, 0.955, 0.922, 0.892, 0.863],
  2: [0.955, 0.922, 0.892, 0.863, 0.837],
  3: [0.922, 0.892, 0.863, 0.837, 0.811],
  4: [0.892, 0.863, 0.837, 0.811, 0.786],
  5: [0.863, 0.837, 0.811, 0.786, 0.762],
  6: [0.837, 0.811, 0.786, 0.762, 0.739],
  7: [0.811, 0.786, 0.762, 0.739, 0.707],
  8: [0.786, 0.762, 0.739, 0.707, 0.680],
  9: [0.762, 0.739, 0.707, 0.680, 0.653],
  10: [0.739, 0.707, 0.680, 0.653, 0.626],
  11: [0.707, 0.680, 0.653, 0.626, 0.599],
  12: [0.680, 0.653, 0.626, 0.599, 0.572],
};
export function pctOf1RM(reps, rpe = 10) {
  const r = Math.max(1, Math.min(12, Math.round(reps)));
  const row = RPE_TABLE[r];
  const x = Math.max(6, Math.min(10, rpe));
  const hiIdx = Math.floor(10 - x);           // 10→0, 9.5→0, 9→1 ...
  const loIdx = Math.min(4, hiIdx + 1);
  const frac = (10 - x) - hiIdx;
  const pct = row[hiIdx] + (row[loIdx] - row[hiIdx]) * frac;
  // Beyond 12 reps extrapolate ~2.7% per rep
  return reps > 12 ? Math.max(0.4, pct - (reps - 12) * 0.027) : pct;
}

// e1RM that accounts for reported effort: 8 reps @ RPE 8 implies more strength
// than 8 reps to failure. Falls back to rep-only estimate without RPE.
export function e1rmRPE(w, reps, rpe) {
  if (!(w > 0) || !(reps > 0)) return 0;
  if (rpe == null || !(rpe >= 6) || reps > 12) return e1rm(w, reps);
  return w / pctOf1RM(reps, rpe);
}

// Load to use for `reps` at target `rpe`, given a current e1RM. Rounded to `inc`.
export function loadFor(e1, reps, rpe, inc = 5) {
  if (!(e1 > 0)) return 0;
  const raw = e1 * pctOf1RM(reps, rpe);
  return Math.max(0, Math.round(raw / inc) * inc);
}

// ——— Set model: load kinds, tracks, set types ——————————————————————————
//
// Exercise.load  'weighted' (default: w is the load) | 'bodyweight' |
//                'weighted-bw' (w is ADDED load) | 'assisted' (w is assistance)
// Exercise.track 'reps' (default) | 'time' (reps = seconds, legacy unit 'sec') |
//                'distance-time' (set.dist metres + set.dur seconds, reps 0)
// Both are optional; exerciseKind() derives sensible defaults from the name,
// equipment and catalog category so built-in and catalog exercises just work.
//
// Bodyweight sets snapshot the lifter's body weight at log time as set.bw (lb);
// older sets fall back to the weigh-in nearest the session.
//
// Set types: 'normal' | 'warmup' | 'drop' | 'failure' | intensity techniques
// 'myo' (activation set), 'restpause', 'cluster', each followed by 'mini' rows
// (set.tech names the technique). A mini-set belongs to the nearest preceding
// non-mini row, so no index links break when rows are inserted or deleted.

export const LOAD_KINDS = ['weighted', 'bodyweight', 'weighted-bw', 'assisted'];
export const TRACKS = ['reps', 'time', 'distance-time'];
export const isBwLoad = (load) => load === 'bodyweight' || load === 'weighted-bw' || load === 'assisted';

const CARDIO_RE = /\b(run|runs|running|jog|jogging|walk|walking|treadmill|cycling|cycle|bike|biking|bicycl\w*|elliptical|stair ?master|stairmaster|step ?mill|stair climb\w*|rowing|row erg|rower|ergometer|ski ?erg|swim\w*|skating|skipping|rope jump\w*|jump rope|hike|hiking|sprints?|ruck\w*|airdyne|assault bike)\b/i;
// "Air bike" in the catalog is the bicycle crunch, "wind sprints" an ab drill.
const NOT_CARDIO_RE = /farmer|carry|lunge|yoke|sled|good morning|monster walk|^air bike$|wind sprint|\bdumbbell\b|\bbarbell\b|kettlebell/i;
const HOLD_RE = /\b(plank|planks|hold|holds|wall sit|l-sit|dead hang|isometric|static|hollow body)\b/i;
const NOT_TIMED_RE = /clean|snatch|jerk|swing|high pull/i;
const BW_RE = /\b(pull-?ups?|pullups|chin-?ups?|chins?|dips?|push-?ups?|pushups|muscle[- ]?ups?|inverted rows?|pistol squats?|nordic|handstand push)/i;
const LOADED_EQ_RE = /machine|cable|smith|barbell|ez|kettlebell|dumbbell|lever|sled|medicine/i;

const _kindCache = new WeakMap();
// { load, track } for an exercise record (and/or its catalog entry).
export function exerciseKind(ex, cat) {
  if (ex && !cat && _kindCache.has(ex)) return _kindCache.get(ex);
  const name = String(ex?.name || cat?.name || '');
  const eq = String(ex?.equipment || cat?.equipment || '');
  const category = ex?.category || cat?.category || null;
  let track = ex?.track || null;
  if (!TRACKS.includes(track)) {
    track = 'reps';
    if (category === 'cardio' || (!category && CARDIO_RE.test(name) && !NOT_CARDIO_RE.test(name))) track = 'distance-time';
    else if ((ex?.unit || cat?.unit) === 'sec' && !NOT_TIMED_RE.test(name)) track = 'time';
    else if (HOLD_RE.test(name) && !NOT_TIMED_RE.test(name) && category !== 'stretch') track = 'time';
  }
  let load = ex?.load || null;
  if (!LOAD_KINDS.includes(load)) {
    load = 'weighted';
    if (track === 'distance-time') load = 'weighted';
    else if (/\bassist(ed)?\b/i.test(name)) load = 'assisted';
    else if (/^bodyweight\+$/i.test(eq)) load = 'weighted-bw';
    else if (BW_RE.test(name) && !LOADED_EQ_RE.test(eq) && category !== 'stretch' && category !== 'plyo') load = /\bweighted\b/i.test(name) ? 'weighted-bw' : 'bodyweight';
  }
  const out = { load, track };
  if (ex && !cat) _kindCache.set(ex, out);
  return out;
}

// Body weight (lb) on a date: the latest weigh-in on or before it, else the
// earliest one after it, else the training profile's, else null.
const _bwCache = new WeakMap();
export function bodyweightAt(state, ts = Date.now()) {
  const list = state && state.bodyEntries;
  if (Array.isArray(list) && list.length) {
    let sorted = _bwCache.get(list);
    if (!sorted) {
      sorted = list.filter(b => b && +b.weight > 0 && b.date)
        .map(b => ({ t: Date.parse(String(b.date).slice(0, 10) + 'T12:00:00'), w: +b.weight }))
        .filter(x => Number.isFinite(x.t)).sort((a, b) => a.t - b.t);
      _bwCache.set(list, sorted);
    }
    if (sorted.length) {
      let pick = sorted[0];
      for (const e of sorted) { if (e.t <= ts + 12 * 3600 * 1000) pick = e; else break; }
      return pick.w;
    }
  }
  const p = state && state.profile && +state.profile.bodyweight;
  return p > 0 ? p : null;
}

// Effective load (lb) actually moved: body weight ± the logged load for
// bodyweight movements. Without a known body weight, bodyweight sets fall back
// to the added load (the pre-bodyweight behaviour) and assisted sets to 0.
export function effectiveLoad(w, load, bw) {
  const x = +w || 0;
  if (load === 'bodyweight' || load === 'weighted-bw') return bw > 0 ? bw + x : Math.max(0, x);
  if (load === 'assisted') return bw > 0 ? Math.max(0, bw - x) : 0;
  return x;
}
// Effective load of a logged set, looking up the body weight when it wasn't snapshotted.
export function setLoad(state, st, kind, at) {
  if (!kind || !isBwLoad(kind.load)) return +st.w || 0;
  const bw = st.bw > 0 ? st.bw : bodyweightAt(state, at ?? st.ts ?? Date.now());
  return effectiveLoad(st.w, kind.load, bw);
}
// Inverse: the number to log (added load / assistance) to move `eff` lb in total.
export function toLoggedLoad(eff, load, bw) {
  if (load === 'bodyweight' || load === 'weighted-bw') return bw > 0 ? Math.max(0, eff - bw) : eff;
  if (load === 'assisted') return bw > 0 ? Math.max(0, bw - eff) : 0;
  return eff;
}

// Hard-set credit by type: warm-ups 0, myo-rep / rest-pause mini-sets ½ each,
// cluster mini-sets 0 (the cluster counts once, on its first block).
export function hardSetValue(st) {
  if (!st || st.type === 'warmup') return 0;
  if (st.type === 'mini') return st.tech === 'cluster' ? 0 : 0.5;
  return 1;
}
// Sets that can stand for strength (e1RM, PRs, "Previous", progression): no
// warm-ups and no mini-sets (a 4-rep mini after 15 s rest says little about 1RM).
export const isStrengthSet = (st) => !!st && st.type !== 'warmup' && st.type !== 'mini' && st.reps > 0;

// ——— Effort scale (RPE ↔ RIR) ——————————————————————————————————————————
// Storage is always RPE; RIR = 10 − RPE is a display choice.
export const rirFromRpe = (rpe) => (rpe == null || !(rpe > 0) ? null : Math.max(0, +(10 - rpe).toFixed(1)));
export const rpeFromRir = (rir) => (rir == null || !(rir >= 0) ? null : Math.min(10, Math.max(1, +(10 - rir).toFixed(1))));
const trimNum = (n) => (Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : n.toFixed(1));
// "@8" or "2 RIR" ("4+ RIR" at RPE 6 and below).
export function effortText(rpe, scale = 'rpe') {
  if (rpe == null || !(rpe > 0)) return '';
  if (scale === 'rir') { const r = rirFromRpe(rpe); return `${r >= 4 ? '4+' : trimNum(r)} RIR`; }
  return `@${trimNum(rpe)}`;
}

// ——— Cardio & timed sets ————————————————————————————————————————————————
const M_PER_MI = 1609.344;
export const distUnit = (units) => (units === 'kg' ? 'km' : 'mi');
export const metersFrom = (v, units) => (+v > 0 ? +v * (units === 'kg' ? 1000 : M_PER_MI) : 0);
export const metersTo = (m, units) => (+m > 0 ? +m / (units === 'kg' ? 1000 : M_PER_MI) : 0);
// "5.00", "12.4" (two decimals under 10).
export function fmtDistance(m, units) {
  const v = metersTo(m, units);
  if (!(v > 0)) return '0';
  return v < 10 ? v.toFixed(2) : v.toFixed(1);
}
// 1530 → "25:30", 3723 → "1:02:03".
export function fmtDuration(s) {
  s = Math.max(0, Math.round(+s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const p = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${p(m)}:${p(sec)}` : `${m}:${p(sec)}`;
}
// "25:30" / "1:02:03" / "90" / "90s" / "1m30" → seconds, or null.
export function parseDuration(text) {
  const t = String(text ?? '').trim().toLowerCase();
  if (!t) return null;
  let m;
  if ((m = t.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/))) return +m[1] * 3600 + +m[2] * 60 + +m[3];
  if ((m = t.match(/^(\d{1,3}):(\d{1,2})$/))) return +m[1] * 60 + +m[2];
  if ((m = t.match(/^(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hours?)$/))) return Math.round(+m[1] * 3600);
  if ((m = t.match(/^(\d+(?:\.\d+)?)\s*(?:m|min|mins|minutes?)$/))) return Math.round(+m[1] * 60);
  if ((m = t.match(/^(\d+)\s*m(?:in)?\s*(\d+)\s*s?(?:ec)?$/))) return +m[1] * 60 + +m[2];
  if ((m = t.match(/^(\d+(?:\.\d+)?)\s*(?:s|sec|secs|seconds?)?$/))) return Math.round(+m[1]);
  return null;
}
// Seconds per km (null without both distance and time).
export function paceSecPerKm(m, s) {
  return +m > 0 && +s > 0 ? +s / (+m / 1000) : null;
}
// "5:06 /km" or "8:13 /mi".
export function fmtPace(secPerKm, units) {
  if (!(secPerKm > 0) || !Number.isFinite(secPerKm)) return '';
  const per = units === 'kg' ? secPerKm : secPerKm * M_PER_MI / 1000;
  return `${fmtDuration(per)} /${distUnit(units)}`;
}
// Seconds → "45 s" under a minute, else "1:30".
export const fmtHold = (s) => (s >= 60 ? fmtDuration(s) : `${Math.round(s)} s`);

// Load text for display (no unit): "185", "BW", "BW + 25", "BW − 40".
export function loadText(w, load, units) {
  const v = (+w || 0) * (units === 'kg' ? 0.45359237 : 1);
  const n = Math.abs(v - Math.round(v)) < 0.05 ? String(Math.round(v)) : v.toFixed(1);
  if (load === 'bodyweight' || load === 'weighted-bw') return v > 0 ? `BW + ${n}` : 'BW';
  if (load === 'assisted') return v > 0 ? `BW − ${n}` : 'BW';
  return n;
}
// One logged set as text: "185 lb × 8", "BW + 25 lb × 8", "BW × 12", "45 s", "5.00 km · 25:30 · 5:06 /km".
// compact: no unit and no inner spaces ("185 × 8", "BW+25 × 8") for chips and tables.
export function setText(st, kind, units, { effort = 'rpe', compact = false } = {}) {
  const k = kind || { load: 'weighted', track: 'reps' };
  const u = units === 'kg' ? 'kg' : 'lb';
  const e = st.rpe ? ` ${effortText(st.rpe, effort)}` : '';
  if (k.track === 'distance-time') {
    const parts = [];
    if (st.dist > 0) parts.push(`${fmtDistance(st.dist, units)} ${distUnit(units)}`);
    if (st.dur > 0) parts.push(fmtDuration(st.dur));
    const p = paceSecPerKm(st.dist, st.dur);
    if (p) parts.push(fmtPace(p, units));
    return parts.join(' · ') || '—';
  }
  const lt = loadText(st.w, k.load, units);
  if (compact) {
    const lp = isBwLoad(k.load) ? lt.replace(/ /g, '') : (+st.w > 0 ? lt : '');
    if (k.track === 'time') return `${lp ? lp + ' · ' : ''}${fmtHold(st.reps || st.dur || 0)}${e}`;
    return lp ? `${lp} × ${st.reps}${e}` : `BW × ${st.reps}${e}`;
  }
  const loadPart = isBwLoad(k.load) ? lt : (+st.w > 0 ? `${lt} ${u}` : '');
  if (k.track === 'time') return `${loadPart ? loadPart + ' · ' : ''}${fmtHold(st.reps || st.dur || 0)}${e}`;
  if (!loadPart) return `${st.reps} rep${st.reps === 1 ? '' : 's'}${e}`;
  return `${loadPart}${isBwLoad(k.load) && +st.w > 0 ? ' ' + u : ''} × ${st.reps}${e}`;
}

// ——— Helpers over sessions ————————————————————————————————————————————

export function completedSessions(state) {
  return (state.sessions || [])
    .filter(s => s && s.completedAt)
    .sort((a, b) => a.startedAt - b.startedAt);
}

// Logged, non-warm-up set (cardio sets have reps 0 but distance / time).
export const isWorkingSet = (st) => !!st && st.type !== 'warmup' && (st.reps > 0 || st.dur > 0 || st.dist > 0);

const _idxCache = new WeakMap();
function cachedIndex(state) {
  const list = state.exercises || [];
  let idx = _idxCache.get(list);
  if (!idx) { idx = indexExercises(state); _idxCache.set(list, idx); }
  return idx;
}
export const kindOf = (state, exerciseId) => exerciseKind(cachedIndex(state)[exerciseId] || { id: exerciseId });

// Per-exercise history: one point per session with the best set.
// → [{ sessionId, date(ms), bestW, bestReps, e1rm, volume, sets, topRpe, maxW,
//      bestEff, maxEff, bw, load, track }]
// e1rm / volume / bestEff / maxEff use the EFFECTIVE load (body weight ± logged
// load for bodyweight movements); bestW / maxW stay the logged number (what you
// would type next time). Timed and cardio exercises carry e1rm 0 — never a fake
// estimate (see cardioHistory for distance / time / pace).
export function exerciseHistory(state, exerciseId) {
  const out = [];
  const kind = kindOf(state, exerciseId);
  const strength = kind.track === 'reps';
  for (const s of completedSessions(state)) {
    const sets = (s.sets || []).filter(st => st.exerciseId === exerciseId && isWorkingSet(st));
    if (!sets.length) continue;
    let best = null;
    let volume = 0;
    let maxEff = 0;
    for (const st of sets) {
      const eff = strength ? setLoad(state, st, kind, s.startedAt) : 0;
      if (strength) volume += eff * (st.reps || 0);
      if (st.type === 'mini' || !(st.reps > 0)) continue;
      if (eff > maxEff) maxEff = eff;
      const est = strength ? e1rmRPE(eff, st.reps, st.rpe) : 0;
      if (!best || est > best.e1rm || (est === best.e1rm && st.reps > best.reps)) best = { e1rm: est, w: st.w, reps: st.reps, rpe: st.rpe, eff };
    }
    if (!best) best = { e1rm: 0, w: sets[0].w || 0, reps: sets[0].reps || 0, rpe: null, eff: 0 };
    const bwKind = isBwLoad(kind.load);
    out.push({
      sessionId: s.id, date: s.startedAt,
      bestW: best.w, bestReps: best.reps, e1rm: best.e1rm, topRpe: best.rpe ?? null,
      volume, sets: sets.length,
      maxW: Math.max(...sets.map(x => x.w || 0)),
      bestEff: best.eff, maxEff,
      bw: bwKind ? (sets.find(x => x.bw > 0)?.bw || bodyweightAt(state, s.startedAt)) : null,
      load: kind.load, track: kind.track,
    });
  }
  return out;
}

// Distance / time history for cardio: one point per session.
// → [{ sessionId, date, dist (m), dur (s), sets, bestPace (s/km), longest (m), longestDur (s) }]
export function cardioHistory(state, exerciseId) {
  const out = [];
  for (const s of completedSessions(state)) {
    const sets = (s.sets || []).filter(st => st.exerciseId === exerciseId && st.type !== 'warmup' && (st.dist > 0 || st.dur > 0));
    if (!sets.length) continue;
    let dist = 0, dur = 0, bestPace = null, longest = 0, longestDur = 0;
    for (const st of sets) {
      dist += +st.dist || 0; dur += +st.dur || 0;
      if (st.dist > longest) longest = +st.dist;
      if (st.dur > longestDur) longestDur = +st.dur;
      // Pace only from real efforts (≥ 400 m) so a 50 m stride isn't a "best pace".
      const p = st.dist >= 400 ? paceSecPerKm(st.dist, st.dur) : null;
      if (p && (bestPace == null || p < bestPace)) bestPace = p;
    }
    out.push({ sessionId: s.id, date: s.startedAt, dist, dur, sets: sets.length, bestPace, longest, longestDur });
  }
  return out;
}

// Rep-max records on effective load: best load achieved for each rep count 1..12.
// { [reps]: { w (effective lb), added (logged), date, reps } }
export function repMaxes(state, exerciseId) {
  const best = {};
  const kind = kindOf(state, exerciseId);
  if (kind.track !== 'reps') return best;
  for (const s of completedSessions(state)) {
    for (const st of (s.sets || [])) {
      if (st.exerciseId !== exerciseId || !isStrengthSet(st)) continue;
      const eff = setLoad(state, st, kind, s.startedAt);
      if (!(eff > 0)) continue;
      const r = Math.min(st.reps, 12);
      for (let k = 1; k <= r; k++) {
        if (!best[k] || eff > best[k].w) best[k] = { w: eff, added: st.w, date: s.startedAt, reps: st.reps };
      }
    }
  }
  return best;
}

// ——— Muscle volume ——————————————————————————————————————————————————

// Weekly hard-set landmarks (sets/week) — loosely after RP volume landmarks.
// MEV = minimum effective, MAV = productive range upper bound, MRV = recoverable max.
export const VOLUME_LANDMARKS = {
  Chest: { mev: 8, mav: 18, mrv: 22 },
  Back: { mev: 10, mav: 20, mrv: 25 },
  Shoulders: { mev: 8, mav: 20, mrv: 26 },
  Quads: { mev: 8, mav: 16, mrv: 20 },
  Hamstrings: { mev: 6, mav: 14, mrv: 20 },
  Glutes: { mev: 4, mav: 12, mrv: 16 },
  Calves: { mev: 8, mav: 14, mrv: 20 },
  Biceps: { mev: 8, mav: 18, mrv: 26 },
  Triceps: { mev: 6, mav: 14, mrv: 18 },
  Core: { mev: 0, mav: 16, mrv: 25 },
  Traps: { mev: 0, mav: 12, mrv: 26 },
  Forearms: { mev: 0, mav: 10, mrv: 20 },
  'Lower Back': { mev: 0, mav: 8, mrv: 12 },
};
export const TRACKED_MUSCLES = Object.keys(VOLUME_LANDMARKS);

// Muscle attribution for an exercise: primary muscles count 1 set, secondary 0.5.
export function musclesFor(ex) {
  if (!ex) return { primary: [], secondary: [] };
  const primary = ex.primary && ex.primary.length ? ex.primary : (ex.group ? [ex.group] : []);
  const secondary = (ex.secondary || []).filter(m => !primary.includes(m));
  return { primary, secondary };
}

// Hard sets per muscle for sessions within [from, to).
export function muscleSets(state, from, to, exIndex) {
  const idx = exIndex || indexExercises(state);
  const out = {};
  for (const s of completedSessions(state)) {
    if (s.startedAt < from || s.startedAt >= to) continue;
    for (const st of (s.sets || [])) {
      if (!isWorkingSet(st)) continue;
      const ex = idx[st.exerciseId];
      // Cardio is not hypertrophy volume; mini-sets count by technique.
      if (exerciseKind(ex || { id: st.exerciseId }).track === 'distance-time') continue;
      const v = hardSetValue(st);
      if (!v) continue;
      const { primary, secondary } = musclesFor(ex);
      primary.forEach(m => { out[m] = (out[m] || 0) + v; });
      secondary.forEach(m => { out[m] = (out[m] || 0) + v * 0.5; });
    }
  }
  return out;
}

export function indexExercises(state) {
  const idx = {};
  for (const e of (state.exercises || [])) idx[e.id] = e;
  return idx;
}

// Start of the current training week (Monday 00:00 local).
export function weekStart(ts = Date.now()) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  return d.getTime();
}

// Status of this week's volume for each tracked muscle.
// Uses the previous 4 complete weeks' average as a baseline so mid-week
// numbers are judged against the user's own pattern, not just the landmarks.
// The baseline averages only the complete weeks the lifter has actually been
// training (up to 4), and nothing is judged "low" or "high" before two complete
// weeks exist: a new user's first week is not a volume problem.
export function volumeReport(state, now = Date.now()) {
  const idx = indexExercises(state);
  const ws = weekStart(now);
  const thisWeek = muscleSets(state, ws, ws + WEEK, idx);
  const prev = [1, 2, 3, 4].map(k => muscleSets(state, ws - k * WEEK, ws - (k - 1) * WEEK, idx));
  const done = completedSessions(state);
  const first = done.length ? weekStart(done[0].startedAt) : ws;
  const weeksAvail = Math.max(0, Math.min(4, Math.round((ws - first) / WEEK)));
  const trained = new Set([...Object.keys(thisWeek), ...prev.flatMap(p => Object.keys(p))]);
  return TRACKED_MUSCLES.filter(m => trained.has(m) || (VOLUME_LANDMARKS[m].mev > 0)).map(m => {
    const lm = VOLUME_LANDMARKS[m];
    const cur = thisWeek[m] || 0;
    const avg4 = weeksAvail ? prev.slice(0, weeksAvail).reduce((a, p) => a + (p[m] || 0), 0) / weeksAvail : 0;
    let status = 'ok';
    if (weeksAvail >= 2) {
      if (avg4 < lm.mev && lm.mev > 0) status = 'low';
      else if (avg4 > lm.mrv) status = 'high';
    }
    return { muscle: m, thisWeek: cur, avg4: +avg4.toFixed(1), weeks: weeksAvail, ...lm, status };
  });
}

// ——— Plateaus & trends ————————————————————————————————————————————————

// Slope of e1RM over time via least squares, in % per week.
export function trendPctPerWeek(points) {
  if (!points || points.length < 3) return null;
  const xs = points.map(p => p.date / WEEK);
  const ys = points.map(p => p.e1rm);
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, den = 0;
  for (let i = 0; i < n; i++) { num += (xs[i] - mx) * (ys[i] - my); den += (xs[i] - mx) ** 2; }
  if (den === 0 || my === 0) return 0;
  return (num / den) / my * 100;
}

// A lift is plateaued when its best e1RM hasn't improved by ≥1% across the last
// `window` exposures spanning at least 3 weeks.
export function plateauFor(history, window = 4) {
  if (history.length < window + 1) return null;
  const recent = history.slice(-window);
  const before = history.slice(0, -window);
  const priorBest = Math.max(...before.map(h => h.e1rm));
  const recentBest = Math.max(...recent.map(h => h.e1rm));
  const span = recent[recent.length - 1].date - recent[0].date;
  if (span < 3 * WEEK - DAY) return null;
  if (recentBest < priorBest * 1.01) {
    return { stalledSince: recent[0].date, exposures: window, priorBest, recentBest };
  }
  return null;
}

// ——— Readiness / fatigue ——————————————————————————————————————————————

// Performance index of a session: mean ratio of each exercise's best e1RM to
// that exercise's rolling best before the session. ~1.0 = on par.
export function sessionPerformance(state, session, historyCache = {}) {
  const exIds = [...new Set((session.sets || []).filter(isWorkingSet).map(s => s.exerciseId))];
  const ratios = [];
  for (const id of exIds) {
    const hist = historyCache[id] || (historyCache[id] = exerciseHistory(state, id));
    const i = hist.findIndex(h => h.sessionId === session.id);
    if (i < 2) continue;
    const prior = hist.slice(Math.max(0, i - 4), i);
    const ref = Math.max(...prior.map(h => h.e1rm));
    if (ref > 0) ratios.push(hist[i].e1rm / ref);
  }
  if (!ratios.length) return null;
  return ratios.reduce((a, b) => a + b, 0) / ratios.length;
}

// Readiness 0–100 plus human-readable reasons. Signals:
//   performance of the last 3 sessions vs rolling bests,
//   RPE creep (same loads feeling harder),
//   training density (sessions in last 7 days vs usual),
//   days since the last session (detraining / freshness).
export function readiness(state, now = Date.now()) {
  const done = completedSessions(state);
  if (done.length < 4) return { score: null, label: 'Calibrating', reasons: ['Log a few more workouts and readiness will calibrate to you.'] };
  const cache = {};
  const recent = done.slice(-3);
  const perfs = recent.map(s => sessionPerformance(state, s, cache)).filter(x => x != null);
  const perf = perfs.length ? perfs.reduce((a, b) => a + b, 0) / perfs.length : 1;

  const rpeOf = (ss) => {
    const r = ss.flatMap(s => (s.sets || []).filter(st => isWorkingSet(st) && st.rpe)).map(st => st.rpe);
    return r.length ? r.reduce((a, b) => a + b, 0) / r.length : null;
  };
  const rpeRecent = rpeOf(recent);
  const rpeBase = rpeOf(done.slice(-12, -3));

  const last7 = done.filter(s => s.startedAt > now - WEEK).length;
  const weeksSpan = Math.max(1, Math.min(8, (now - done[0].startedAt) / WEEK));
  const usual = done.filter(s => s.startedAt > now - weeksSpan * WEEK).length / weeksSpan;
  const daysSince = (now - done[done.length - 1].completedAt) / DAY;

  let score = 75;
  const reasons = [];
  const perfDelta = (perf - 1) * 100;
  score += Math.max(-25, Math.min(15, perfDelta * 3));
  if (perfDelta <= -3) reasons.push(`Recent top sets are ${Math.abs(perfDelta).toFixed(1)}% below your rolling bests.`);
  else if (perfDelta >= 1.5) reasons.push(`You're lifting ${perfDelta.toFixed(1)}% above your recent bests — good momentum.`);

  if (rpeRecent != null && rpeBase != null) {
    const creep = rpeRecent - rpeBase;
    score -= Math.max(0, creep) * 8;
    if (creep >= 0.75) reasons.push(`Effort is creeping up (avg RPE ${rpeBase.toFixed(1)} → ${rpeRecent.toFixed(1)}).`);
  }
  if (usual > 0 && last7 > usual * 1.4 && last7 >= 4) {
    score -= 8;
    reasons.push(`${last7} sessions in 7 days is above your usual ${usual.toFixed(1)}/week.`);
  }
  if (daysSince >= 5) {
    score += 5;
    reasons.push(`${Math.floor(daysSince)} days since your last session — you should be fresh; ease into the first sets.`);
  } else if (daysSince < 0.8) {
    score -= 5;
  }
  score = Math.round(Math.max(5, Math.min(100, score)));
  const label = score >= 80 ? 'Primed' : score >= 65 ? 'Ready' : score >= 45 ? 'Fatigued' : 'Run down';
  // The sentence always matches the label: a low score names what pulled it down.
  if (score < 65) {
    if (perfDelta < -0.5 && perfDelta > -3) reasons.push(`Top sets are slightly below your rolling bests (${perfDelta.toFixed(1)}%).`);
    if (rpeRecent != null && rpeBase != null && rpeRecent - rpeBase > 0.25 && rpeRecent - rpeBase < 0.75) reasons.push(`The same work is feeling a little harder (RPE ${rpeBase.toFixed(1)} → ${rpeRecent.toFixed(1)}).`);
    if (daysSince < 0.8) reasons.push('Less than a day since your last session.');
    if (!reasons.length) reasons.push('Several small signals add up: keep today\'s sets crisp and stop short of failure.');
  }
  if (!reasons.length) reasons.push('Performance and effort are in line with your normal range.');
  return { score, label, reasons, perf, rpeRecent, rpeBase, last7 };
}

// Deload recommendation: several stalled lifts at once, sustained RPE creep,
// or 5+ hard weeks since the last light week.
// A program with a deload built into its cycle (or an RP-style meso) handles
// its own light weeks, so generic advice is held back for it.
export function plannedDeload(state) {
  const prog = (state.programs || []).find(p => p && p.id === state.activeProgramId);
  if (!prog) return false;
  const weeks = (prog.cycle && prog.cycle.weeks) || (prog.periodization && prog.periodization.weeks) || [];
  if (weeks.some(w => w && (w.deload || /deload/i.test(w.label || '')))) return true;
  return (prog.days || []).some(d => (d.exercises || []).some(pe => pe && pe.scheme === 'rp_sets'));
}

export function deloadAdvice(state, now = Date.now()) {
  const done = completedSessions(state);
  if (done.length < 8) return null;
  if (plannedDeload(state)) return { recommend: false, planned: true, reasons: ['Your program has a deload week built in.'], hardWeeks: 0, stalledCount: 0 };
  const idx = indexExercises(state);
  const exIds = [...new Set(done.slice(-10).flatMap(s => (s.sets || []).map(st => st.exerciseId)))];
  const stalled = exIds.map(id => ({ id, p: plateauFor(exerciseHistory(state, id)) })).filter(x => x.p);
  const r = readiness(state, now);

  // Consecutive weeks with ≥ 2 sessions (no light week)
  let hardWeeks = 0;
  const ws = weekStart(now);
  for (let k = 1; k <= 12; k++) {
    const n = done.filter(s => s.startedAt >= ws - k * WEEK && s.startedAt < ws - (k - 1) * WEEK).length;
    if (n >= 2) hardWeeks++; else break;
  }
  const reasons = [];
  if (stalled.length >= 3) reasons.push(`${stalled.length} lifts have stalled (${stalled.slice(0, 3).map(s => idx[s.id]?.name || 'lift').join(', ')}).`);
  if (r.score != null && r.score < 50) reasons.push(`Readiness is low (${r.score}).`);
  if (hardWeeks >= 6) reasons.push(`${hardWeeks} straight hard weeks without a lighter one.`);
  if (reasons.length >= 2 || (hardWeeks >= 8)) {
    return {
      recommend: true,
      reasons,
      prescription: 'Next week: keep the same exercises and weights, cut working sets by ~40% and stop 3+ reps short of failure (RPE ≤ 7). Then resume normal progression.',
    };
  }
  return { recommend: false, reasons, hardWeeks, stalledCount: stalled.length };
}

// ——— Consistency ——————————————————————————————————————————————————————

// Weekly streak: consecutive weeks (ending this week or last) with ≥ target sessions.
export function weeklyStreak(state, target = 1, now = Date.now()) {
  const done = completedSessions(state);
  const ws = weekStart(now);
  const count = (k) => done.filter(s => s.startedAt >= ws - k * WEEK && s.startedAt < ws - (k - 1) * WEEK).length;
  // The current week counts once its target is met; otherwise start from last week.
  let streak = 0;
  for (let k = count(0) >= target ? 0 : 1; k < 520 && count(k) >= target; k++) streak++;
  return streak;
}

// ——— Weekly summary (feeds home insight card + LLM coach context) ————————

export function trainingSnapshot(state, now = Date.now()) {
  const done = completedSessions(state);
  const idx = indexExercises(state);
  const exIds = [...new Set(done.flatMap(s => (s.sets || []).map(st => st.exerciseId)))];
  const lifts = exIds.map(id => {
    if (kindOf(state, id).track !== 'reps') return null;
    const h = exerciseHistory(state, id);
    if (!h.length) return null;
    const last = h[h.length - 1];
    const recent = h.filter(p => p.date > now - 8 * WEEK);
    const lb = isBwLoad(last.load) ? loadText(last.bestW, last.load, 'lb').replace(/ /g, '') : String(Math.round(last.bestW));
    return {
      id, name: idx[id]?.name || id,
      exposures: h.length,
      lastDate: last.date,
      lastBest: `${lb}×${last.bestReps}${last.topRpe ? ' @' + last.topRpe : ''}`,
      e1rm: Math.round(last.e1rm),
      bestE1rm: Math.round(Math.max(...h.map(p => p.e1rm))),
      trendPctPerWeek: trendPctPerWeek(recent),
      plateau: !!plateauFor(h),
    };
  }).filter(Boolean).sort((a, b) => b.exposures - a.exposures);
  return {
    sessionsTotal: done.length,
    sessionsLast4Weeks: done.filter(s => s.startedAt > now - 4 * WEEK).length,
    streakWeeks: weeklyStreak(state, 1, now),
    readiness: readiness(state, now),
    volume: volumeReport(state, now),
    deload: deloadAdvice(state, now),
    lifts,
  };
}

// ——— Per-muscle feedback (RP / Alpha-style) ————————————————————————————————
//
// session.feedback = { [muscle]: { pump, soreness, joint, jointEx? } }
//   pump      'low' | 'ok' | 'great'            how pumped the muscle got today
//   soreness  'none' | 'healed' | 'sore'        soreness from LAST time: never sore,
//                                               healed just in time, still sore
//   joint     'none' | 'some' | 'bad'           joint pain during today's sets
//   jointEx   exercise ids that hurt (optional; only when joint isn't 'none')
// Any answer may be null (skipped question). session.feedbackSkipped lists
// muscles the lifter dismissed. Progression (progression.js) reads the same shape.

export const FEEDBACK_OPTIONS = {
  pump: [['low', 'Low'], ['ok', 'Moderate'], ['great', 'Great']],
  soreness: [['none', 'None'], ['healed', 'Healed'], ['sore', 'Still sore']],
  joint: [['none', 'None'], ['some', 'Some'], ['bad', 'Bad']],
};
const FB_SCORE = {
  pump: { low: 0, ok: 1, great: 2 },
  soreness: { none: 0, healed: 1, sore: 2 },
  joint: { none: 0, some: 1, bad: 2 },
};

// Trend of one muscle's answers across completed sessions (oldest → newest),
// with an RP-style set-count signal from the latest answer:
//   joint pain 'bad' or still sore      → 'reduce'
//   healed just in time / great pump    → 'hold'
//   never sore and pump low / moderate  → 'add'
export function muscleFeedbackTrend(state, muscle, { limit = 6 } = {}) {
  const all = [];
  for (const s of completedSessions(state)) {
    const f = s.feedback && s.feedback[muscle];
    if (!f || (f.pump == null && f.soreness == null && f.joint == null)) continue;
    all.push({ sessionId: s.id, date: s.startedAt, pump: f.pump ?? null, soreness: f.soreness ?? null, joint: f.joint ?? null });
  }
  const entries = all.slice(-limit);
  const avg = (key) => {
    const v = entries.map(e => FB_SCORE[key][e[key]]).filter(x => x != null);
    return v.length ? +(v.reduce((a, b) => a + b, 0) / v.length).toFixed(2) : null;
  };
  const last = entries[entries.length - 1] || null;
  let signal = null, reason = null;
  if (last) {
    if (last.joint === 'bad') { signal = 'reduce'; reason = 'Joint pain last session'; }
    else if (last.soreness === 'sore') { signal = 'reduce'; reason = 'Still sore from the session before'; }
    else if (last.soreness === 'healed') { signal = 'hold'; reason = 'Recovered just in time'; }
    else if (last.pump === 'great') { signal = 'hold'; reason = 'Great pump at the current volume'; }
    else if (last.soreness === 'none' || last.pump === 'low') { signal = 'add'; reason = last.soreness === 'none' ? 'Recovered early with room to spare' : 'Low pump'; }
    else { signal = 'hold'; reason = 'Moderate pump'; }
  }
  return { muscle, entries, count: all.length, last, pump: avg('pump'), soreness: avg('soreness'), joint: avg('joint'), signal, reason };
}

// Did this exercise hurt a joint the last time it was done? → { sessionId, date, muscle } | null
export function jointFlagFor(state, exerciseId) {
  const done = completedSessions(state);
  const ex = cachedIndex(state)[exerciseId];
  const prim = musclesFor(ex).primary;
  for (let i = done.length - 1; i >= 0; i--) {
    const s = done[i];
    if (!(s.sets || []).some(st => st.exerciseId === exerciseId && isWorkingSet(st))) continue;
    for (const [m, f] of Object.entries(s.feedback || {})) {
      if (!f || f.joint !== 'bad') continue;
      const hit = Array.isArray(f.jointEx) && f.jointEx.length ? f.jointEx.includes(exerciseId) : prim.includes(m);
      if (hit) return { sessionId: s.id, date: s.startedAt, muscle: m };
    }
    return null; // most recent exposure was pain-free
  }
  return null;
}

// Primary muscles of a live workout, in order, with whether every planned row
// that trains them is logged. → [{ muscle, slotIds, lastSlotId, done, logged }]
export function sessionMuscles(session, slots, exIndex) {
  const out = new Map();
  const logged = new Set((session.sets || []).map(st => st.peId + ':' + st.setIdx));
  for (const sl of slots || []) {
    const ex = exIndex && exIndex[sl.exerciseId];
    const kind = exerciseKind(ex || { name: sl.name, unit: sl.unit });
    if (kind.track === 'distance-time') continue;
    const prim = musclesFor(ex || { group: sl.group }).primary;
    for (const m of prim) {
      if (!m || m === 'Other') continue;
      const cur = out.get(m) || { muscle: m, slotIds: [], lastSlotId: null, done: true, logged: 0 };
      cur.slotIds.push(sl.id);
      cur.lastSlotId = sl.id;
      sl.rows.forEach((r, i) => {
        if (r.type === 'warmup') return;
        if (logged.has(sl.id + ':' + i)) cur.logged++; else cur.done = false;
      });
      out.set(m, cur);
    }
  }
  return [...out.values()];
}

// Muscles to ask about now: finished (or, at finish, any trained) and not yet
// answered or skipped.
export function feedbackDue(session, slots, exIndex, { atFinish = false } = {}) {
  const asked = new Set([...Object.keys(session.feedback || {}), ...(session.feedbackSkipped || [])]);
  return sessionMuscles(session, slots, exIndex).filter(m => m.logged > 0 && !asked.has(m.muscle) && (atFinish || m.done));
}

// Tonnage (lb) of a list of sets on effective load: working sets of rep-based
// exercises only (timed holds and cardio carry no tonnage).
export function setsVolume(state, sets, at) {
  let v = 0;
  for (const st of sets || []) {
    if (!st || st.type === 'warmup' || !(st.reps > 0)) continue;
    const kind = kindOf(state, st.exerciseId);
    if (kind.track !== 'reps') continue;
    v += setLoad(state, st, kind, at ?? st.ts) * st.reps;
  }
  return v;
}
